import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, TrendingUp, MapPin, BarChart3, Info, ArrowUpRight, Building2, Users } from 'lucide-react';
import { api } from '../api';

function fmtPct(v, digits = 1) {
  if (v == null || isNaN(v)) return '—';
  return `${Number(v).toFixed(digits)}%`;
}
function fmtInt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function Donut({ value, color = '#4A7C5E', label, sub }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = 36, c = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-4">
      <svg width="92" height="92" viewBox="0 0 92 92">
        <circle cx="46" cy="46" r={r} stroke="#F0E7D2" strokeWidth="10" fill="none" />
        <circle cx="46" cy="46" r={r} stroke={color} strokeWidth="10" fill="none"
                strokeLinecap="round" strokeDasharray={c}
                strokeDashoffset={c - (v / 100) * c}
                transform="rotate(-90 46 46)" />
        <text x="46" y="50" textAnchor="middle" className="font-display"
              style={{ fontSize: '18px', fill: '#1A2622', fontWeight: 600 }}>
          {value != null ? `${Number(value).toFixed(0)}%` : '—'}
        </text>
      </svg>
      <div>
        <div className="text-[12.5px] font-semibold text-[#1A2622]">{label}</div>
        {sub && <div className="text-[11px] text-[#7A857F] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function Sparkline({ years, accessor = 'ma_beneficiaries', color = '#4A7C5E' }) {
  const data = (years || []).map(y => ({ x: y.year, v: y[accessor] }));
  const valid = data.filter(d => d.v != null);
  if (valid.length < 2) return <div className="text-[11px] text-[#A8B0AA]">No trend data</div>;
  const w = 220, h = 56, pad = 4;
  const min = Math.min(...valid.map(d => d.v));
  const max = Math.max(...valid.map(d => d.v));
  const range = max - min || 1;
  const pts = valid.map((d, i) => {
    const x = pad + (i / (valid.length - 1)) * (w - pad * 2);
    const y = h - pad - ((d.v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} className="block">
      <polyline fill="none" stroke={color} strokeWidth="2" points={pts.join(' ')} />
      {valid.map((d, i) => {
        const x = pad + (i / (valid.length - 1)) * (w - pad * 2);
        const y = h - pad - ((d.v - min) / range) * (h - pad * 2);
        return <circle key={i} cx={x} cy={y} r="2.5" fill={color} />;
      })}
    </svg>
  );
}

export default function ManagedCareTab({ ccn }) {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.maFacility(ccn)
      .then(d => {
        if (cancelled) return;
        setData(d);
        // Pull state trend for sparkline + dual mix history
        if (d?.state?.code) {
          api.maState(d.state.code).then(s => { if (!cancelled) setTrend(s.trend || []); }).catch(() => {});
        }
        setLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Managed care data not available');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [ccn]);

  if (loading) {
    return (
      <div className="mt-7 rounded-xl bg-white border border-[#E5DCC4] p-8 text-[13px] text-[#7A857F]" data-testid="ma-tab-loading">
        Loading managed care intelligence…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="mt-7 rounded-xl bg-white border border-dashed border-[#E5DCC4] p-8 text-center" data-testid="ma-tab-empty">
        <Activity size={22} className="text-[#A8B0AA] mx-auto" />
        <div className="mt-3 text-[13px] text-[#7A857F]">{error || 'Managed care data is not available for this facility.'}</div>
      </div>
    );
  }

  const opp = data.opportunity || {};
  const county = data.county || {};
  const state = data.state || {};
  const pen = data.penetration_pct;
  const isCountyScope = data.penetration_scope === 'county';

  return (
    <section className="mt-7" data-testid="ma-tab">
      {/* Hero strip */}
      <div className="rounded-xl border border-[#E5DCC4] bg-white p-5 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
          <Donut
            value={pen}
            color={opp.color || '#4A7C5E'}
            label={isCountyScope ? `${county.name || ''} County MA penetration` : `${state.name || ''} MA penetration`}
            sub={isCountyScope ? `${fmtInt(county.enrolled)} of ${fmtInt(county.eligibles)} Medicare eligibles enrolled in MA` : 'Facility-county penetration unavailable — showing state benchmark'}
          />
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider"
                 style={{ background: `${opp.color}1A`, color: opp.color }}>
              <BarChart3 size={12} /> {opp.label || 'Unknown'} {opp.score != null && <span className="opacity-75">· score {opp.score}</span>}
            </div>
            <p className="mt-3 text-[13px] text-[#3A4A44] leading-relaxed">{opp.description}</p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[12px]">
              <span className="text-[#5A6660]">National avg: <span className="font-semibold text-[#1A2622]">{fmtPct(state.national_avg_penetration)}</span></span>
              {state.vs_national_delta != null && (
                <span className="text-[#5A6660]">vs national: <span className={`font-semibold ${state.vs_national_delta >= 0 ? 'text-[#C75D5D]' : 'text-[#4A7C5E]'}`}>{state.vs_national_delta > 0 ? '+' : ''}{state.vs_national_delta} pp</span></span>
              )}
              {state.national_rank && (
                <span className="text-[#5A6660]">State rank: <span className="font-semibold text-[#1A2622]">#{state.national_rank}</span></span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Three context cards */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]" data-testid="ma-card-county">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><MapPin size={13}/> County market</div>
          <div className="mt-3 space-y-2 text-[12.5px]">
            <Row k="County" v={county.name || '—'} />
            <Row k="Medicare eligibles" v={fmtInt(county.eligibles)} />
            <Row k="MA enrolled" v={fmtInt(county.enrolled)} />
            <Row k="MA penetration" v={fmtPct(county.penetration_pct, 2)} accent />
            {county.eligibles && county.enrolled && (
              <Row k="FFS opportunity pool" v={fmtInt(county.eligibles - county.enrolled)} />
            )}
          </div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]" data-testid="ma-card-state">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><TrendingUp size={13}/> State context — {state.name}</div>
          <div className="mt-3 space-y-2 text-[12.5px]">
            <Row k="State MA penetration" v={fmtPct(state.penetration_pct, 2)} accent />
            <Row k="Dual-eligible mix" v={state.pct_dual_eligible != null ? fmtPct(state.pct_dual_eligible * 100) : '—'} />
            <Row k="SNF utilization (MA)" v={state.snf_utilization_pct != null ? fmtPct(state.snf_utilization_pct * 100, 2) : '—'} />
            <Row k="SNF days / 1k" v={state.snf_days_per_1000 != null ? Math.round(state.snf_days_per_1000).toLocaleString() : '—'} />
            <Row k={`MA growth ${state.years_span || ''}`} v={state.trend_pct_change != null ? `${state.trend_pct_change > 0 ? '+' : ''}${state.trend_pct_change}%` : '—'} />
          </div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]" data-testid="ma-card-trend">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><Activity size={13}/> MA beneficiaries trend</div>
          <div className="mt-3">
            <Sparkline years={trend} accessor="ma_beneficiaries" color={opp.color || '#4A7C5E'} />
            <div className="mt-2 flex justify-between text-[10.5px] text-[#A8B0AA]">
              <span>{trend[0]?.year || ''}</span>
              <span>{trend[trend.length - 1]?.year || ''}</span>
            </div>
            <div className="mt-2 text-[11.5px] text-[#5A6660]">
              {trend.length >= 2 && trend[0]?.ma_beneficiaries && trend[trend.length - 1]?.ma_beneficiaries ? (
                <>From <strong>{fmtInt(trend[0].ma_beneficiaries)}</strong> ({trend[0].year}) to <strong>{fmtInt(trend[trend.length - 1].ma_beneficiaries)}</strong> ({trend[trend.length - 1].year})</>
              ) : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Top MA plans in this county */}
      {data.plans && (data.plans.top_organizations || []).length > 0 && (
        <div className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden" data-testid="ma-card-plans">
          <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
            <Building2 size={14} className="text-[#4A7C5E]"/>
            <h3 className="font-display text-base font-semibold text-[#1A2622]">MA plans active in this market</h3>
            <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">
              {data.plans.unique_payers} payers · {fmtInt(data.plans.total_enrolled)} enrolled · HHI {data.plans.hhi}
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <ul className="divide-y divide-[#F0E7D2]">
              {data.plans.top_organizations.slice(0, 8).map((o, i) => (
                <li key={`${o.organization}-${i}`} className="px-4 py-2.5 flex items-center gap-3" data-testid={`ma-plan-row-${i}`}>
                  <span className="w-5 text-[11px] text-[#7A857F]">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium text-[#1A2622] truncate">{o.organization}</div>
                    <div className="h-1.5 mt-1 bg-[#F0E7D2] rounded-full overflow-hidden">
                      <div className="h-full bg-[#4A7C5E]" style={{ width: `${Math.min(100, o.share_pct)}%` }}/>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[12.5px] font-semibold text-[#1A2622] tabular-nums">{fmtPct(o.share_pct, 1)}</div>
                    <div className="text-[10.5px] text-[#7A857F]">{fmtInt(o.enrolled)}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="p-5 border-l border-[#F0E7D2]">
              <div className="text-[11px] uppercase tracking-wider text-[#4A7C5E] flex items-center gap-1.5"><Users size={12}/> Plan-type mix</div>
              <div className="mt-3 space-y-2">
                {(data.plans.plan_type_breakdown || []).slice(0, 6).map((p) => (
                  <div key={p.plan_type}>
                    <div className="flex items-center justify-between text-[11.5px] text-[#3A4A44]">
                      <span>{p.plan_type}</span>
                      <span className="font-semibold text-[#1A2622]">{fmtPct(p.share_pct, 1)}</span>
                    </div>
                    <div className="h-1.5 mt-1 bg-[#F0E7D2] rounded-full overflow-hidden">
                      <div className="h-full bg-[#D4A85C]" style={{ width: `${Math.min(100, p.share_pct)}%` }}/>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[10.5px] text-[#A8B0AA]">
                HHI (Herfindahl–Hirschman Index): {data.plans.hhi} —{' '}
                {data.plans.hhi < 1500 ? 'competitive market' : data.plans.hhi < 2500 ? 'moderately concentrated' : 'highly concentrated'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {state.code && (
          <Link to={`/managed-care/state/${state.code}`} className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#2D5F4F]" data-testid="ma-link-state">
            View full {state.name} MA market <ArrowUpRight size={13}/>
          </Link>
        )}
        <Link to="/managed-care" className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md bg-white border border-[#E5DCC4] text-[12.5px] text-[#3A4A44] hover:border-[#4A7C5E]" data-testid="ma-link-dashboard">
          Managed Care dashboard <ArrowUpRight size={13}/>
        </Link>
      </div>

      <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
        <Info size={12} className="mt-0.5 shrink-0" />
        <span>{data.methodology} Source: {data.source}.</span>
      </div>
    </section>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-[#F0E7D2] last:border-0">
      <span className="text-[#5A6660] text-[12px]">{k}</span>
      <span className={`text-[12.5px] font-semibold ${accent ? 'text-[#4A7C5E]' : 'text-[#1A2622]'}`}>{v}</span>
    </div>
  );
}
